// IST-correct rendering of the timestamps the backend hands us.
//
// The API returns datetimes in two shapes, and they mean different things:
//
//   "2026-08-13T09:22:36.691350Z"   an instant, marked UTC
//   "2026-08-13T09:22:36.691350"    ALSO an instant in UTC, but unmarked
//
// The unmarked form is not a different convention — it is the same UTC instant
// with the marker lost on the way out. Django runs with TIME_ZONE="UTC" and
// USE_TZ=True, so timezone.now() produces an aware UTC value; but a number of
// tenant tables (care_plan_tasks, main_ipd_admission, shift_handover,
// main_bed_status_audit, main_patient_bed_transfer_audit, main_ipd_queue,
// care_plan_data ...) were created with `timestamp without time zone` columns.
// Postgres drops the offset on write, psycopg2 hands Django a naive value back,
// and DRF's encoder emits isoformat() with no "Z" to append.
//
// A browser reads that unmarked string as LOCAL time. In IST that renders every
// audit/event timestamp 5h30m earlier than it happened — a dose given at 14:48
// displays as 09:18. parseInstant() restores the missing marker so the instant
// is read correctly whichever shape it arrives in.
//
// Scheduling fields are the exception and must NOT go through parseInstant.
// care_plan_tasks.scheduled_datetime / end_datetime hold the wall-clock time a
// human picked — the "08:00 round" is 08:00 on the ward clock, not an instant —
// and the backend stores those digits verbatim from what the client sent. Read
// as UTC they would all shift to 13:30. Use the WallClock helpers for those:
// they work on the string's own digits and never build a Date, so they stay
// exact no matter how the viewing machine's clock is configured.
//
// Formatting always pins Asia/Kolkata rather than trusting the browser, so a
// mis-configured ward workstation still shows ward time.

export const IST_TIME_ZONE = "Asia/Kolkata";

// Z, +05:30, +0530 or -08:00 — anything that pins the string to a zone.
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a backend timestamp as the instant it represents.
 * An unmarked string is treated as UTC — see the note above on why that is the
 * missing marker rather than a guess. Returns null for anything unparseable.
 */
export function parseInstant(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value == null || value === "") return null;

  const text = String(value).trim();
  if (!text) return null;

  // A bare date carries no instant; local midnight keeps it on the day written.
  if (DATE_ONLY.test(text)) {
    const [y, m, d] = text.split("-").map(Number);
    const local = new Date(y, m - 1, d);
    return Number.isNaN(local.getTime()) ? null : local;
  }

  const marked = HAS_ZONE.test(text) ? text : `${text.replace(" ", "T")}Z`;
  const parsed = new Date(marked);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function istParts(value) {
  const date = parseInstant(value);
  if (!date) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const out = {};
  parts.forEach((p) => { out[p.type] = p.value; });
  // en-GB renders midnight as "24" in some ICU versions; normalise it.
  if (out.hour === "24") out.hour = "00";
  return out;
}

/** "2026-08-13" — the IST calendar day an instant falls on. Use for grouping. */
export function istDayKey(value) {
  const p = istParts(value);
  return p ? `${p.year}-${p.month}-${p.day}` : "";
}

/** "14:48" — IST clock time of an instant. */
export function formatIstTime(value, fallback = "") {
  const p = istParts(value);
  return p ? `${p.hour}:${p.minute}` : fallback;
}

/** "13 Aug 2026" — IST calendar date of an instant. */
export function formatIstDate(value, fallback = "") {
  const date = parseInstant(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIME_ZONE, day: "2-digit", month: "short", year: "numeric",
  }).format(date);
}

/** "13 Aug 2026, 14:48" — the default for audit/event timestamps. */
export function formatIstDateTime(value, fallback = "") {
  const date = parseInstant(value);
  if (!date) return fallback;
  const p = istParts(date);
  return `${formatIstDate(date)}, ${p.hour}:${p.minute}`;
}

// --- Wall-clock fields (scheduled_datetime / end_datetime) -----------------
// These read the digits the backend stored, with no timezone conversion at all.

/** "08:00" — the scheduled slot exactly as it was booked. */
export function formatWallClockTime(value, fallback = "") {
  if (!value) return fallback;
  const match = String(value).match(/[T ](\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : fallback;
}

/** "2026-08-13" — the day a slot was booked for. Use for grouping. */
export function wallClockDayKey(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

/**
 * Re-express an instant as an IST wall-clock string ("2026-08-13T12:37").
 * For the boundaries where a screen has only a wall-clock field to put a value
 * in but the value it fell back to is an instant — normalising here keeps the
 * screen's one-kind-of-time assumption true instead of leaking UTC into it.
 */
export function toIstWallClock(value) {
  const p = istParts(value);
  return p ? `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}` : "";
}
